import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronLeft, ChevronRight, Database, LogOut, X } from 'lucide-react';
import { toast } from 'sonner';
import { BrandMark } from '../BrandMark';
import { useAuth } from '../../contexts/AuthContext';
import { navItems } from '../../data/navigation';
interface SidebarProps {
  collapsed: boolean;
  onToggleCollapsed: () => void;
  mobileOpen: boolean;
  onCloseMobile: () => void;
  totalStudents: number;
}
function NavItems({
  collapsed,
  onNavigate,
  idPrefix
}: {
  collapsed: boolean;
  onNavigate?: () => void;
  idPrefix: string;
}) {
  return <nav aria-label="Main navigation" className="flex flex-col gap-1.5 px-3" data-id="element-170" data-mp="3aq1doqrqpkci">
      {navItems.map(item => <NavLink key={item.to} to={item.to} onClick={onNavigate} title={collapsed ? item.label : undefined} className={({
      isActive
    }) => ['group relative flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-all', 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand', collapsed ? 'justify-center' : '', isActive ? 'bg-white/70 text-foreground shadow-sm dark:bg-white/10' : 'text-muted-foreground hover:bg-white/50 hover:text-foreground dark:hover:bg-white/5'].join(' ')} data-id="element-171" data-mp="1bdp31pmc4crk">
          {({
        isActive
      }) => <>
              {isActive && <motion.span layoutId={`${idPrefix}-active`} className="absolute inset-y-1.5 left-0 w-1 rounded-full brand-gradient" transition={{
          type: 'spring',
          stiffness: 360,
          damping: 30
        }} data-id="element-172" data-mp="25y09g5t6qdd5" />}
              <item.icon className={`h-[18px] w-[18px] shrink-0 ${isActive ? 'text-brand' : ''}`} strokeWidth={2} data-id="element-173" data-mp="ugqrxotm9veh" />
              {!collapsed && <span className="truncate" data-id="element-174" data-mp="r0nyjuy0z293">{item.label}</span>}
            </>}
        </NavLink>)}
    </nav>;
}
function LogoutButton({
  collapsed
}: {
  collapsed: boolean;
}) {
  const {
    logout
  } = useAuth();
  const navigate = useNavigate();
  const handleLogout = async () => {
    try {
      await logout();
      toast.success('Logged out safely.');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not log out.');
    }
    navigate('/', {
      replace: true
    });
  };
  return <button type="button" onClick={handleLogout} title="Log out" className={`mx-3 flex items-center gap-2 rounded-xl border border-[var(--glass-hairline)] py-2.5 text-sm font-medium text-muted-foreground transition-colors hover:border-destructive/40 hover:text-destructive focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand ${collapsed ? 'justify-center' : 'justify-center px-3'}`} data-id="element-175" data-mp="d2zayfckph0c">
      <LogOut className="h-4 w-4" data-id="element-176" data-mp="36u97tg97gr7z" />
      {!collapsed && <span data-id="element-177" data-mp="36v130pyw4mxq">Log out</span>}
    </button>;
}
function LiveBadge({
  totalStudents,
  collapsed
}: {
  totalStudents: number;
  collapsed: boolean;
}) {
  if (collapsed) {
    return <div className="mx-3 grid place-items-center rounded-xl border border-[var(--glass-hairline)] py-3" data-id="element-178" data-mp="3fw8og7gb5tae">
        <Database className="h-4 w-4 text-brand" data-id="element-179" data-mp="3bssx9qn0cl53" />
      </div>;
  }
  return <div className="mx-3 rounded-2xl border border-[var(--glass-hairline)] bg-white/40 p-3 dark:bg-white/5" data-id="element-180" data-mp="3psli55bus755">
      <div className="flex items-center gap-2" data-id="element-181" data-mp="3kt8tqg5c2s0g">
        <span className="relative grid h-2 w-2 place-items-center" data-id="element-182" data-mp="3ksfvk3kfxatx">
          <span className="absolute h-2 w-2 rounded-full bg-emerald-500 animate-pulse-ring" data-id="element-183" data-mp="3d938peiiyoz" />
          <span className="h-2 w-2 rounded-full bg-emerald-500" data-id="element-184" data-mp="3uklq9l22u6ct" />
        </span>
        <p className="text-xs font-semibold text-foreground" data-id="element-185" data-mp="cfiejhhluw3f">Supabase live</p>
      </div>
      <p className="mt-1.5 text-xs text-muted-foreground" data-id="element-186" data-mp="7g5ji9gkyipr">
        <span className="font-mono font-semibold text-foreground" data-id="element-187" data-mp="7djccaxlq582">{totalStudents}</span> record
        {totalStudents === 1 ? '' : 's'} loaded from the <span className="font-mono" data-id="element-188" data-mp="26tnlejm6l7er">students</span> table.
      </p>
    </div>;
}
export function Sidebar({
  collapsed,
  onToggleCollapsed,
  mobileOpen,
  onCloseMobile,
  totalStudents
}: SidebarProps) {
  return <>
      {/* Desktop rail */}
      <aside className={`glass fixed inset-y-0 left-0 z-30 hidden flex-col rounded-r-3xl border-l-0 py-5 transition-[width] duration-300 lg:flex ${collapsed ? 'w-[84px]' : 'w-[264px]'}`} data-id="element-189" data-mp="2fp3prukkb4yl">
        <div className={`mb-6 flex items-center px-4 ${collapsed ? 'justify-center' : 'justify-between'}`} data-id="element-190" data-mp="1rxogfhudbndu">
          <BrandMark size="sm" withText={!collapsed} data-id="element-191" data-mp="1wx123z9i6oeu" />
        </div>

        <NavItems collapsed={collapsed} idPrefix="rail" data-id="element-192" data-mp="1wu54kc1ki1qx" />

        <div className="mt-auto flex flex-col gap-3" data-id="element-193" data-mp="33bs7af5nsveo">
          <LiveBadge totalStudents={totalStudents} collapsed={collapsed} data-id="element-194" data-mp="38bofyrpaxb0k" />
          <LogoutButton collapsed={collapsed} data-id="element-195" data-mp="38drbfr983wco" />
          <button type="button" onClick={onToggleCollapsed} className="mx-3 flex items-center justify-center gap-2 rounded-xl border border-[var(--glass-hairline)] py-2 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand" aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'} data-id="element-196" data-mp="3ifv1xmem80te">
            {collapsed ? <ChevronRight className="h-4 w-4" data-id="element-197" data-mp="2zmr6bvhu7dw5" /> : <ChevronLeft className="h-4 w-4" data-id="element-198" data-mp="2upr7a4jenbej" />}
            {!collapsed && <span data-id="element-199" data-mp="2pqxukebxzoff">Collapse</span>}
          </button>
        </div>
      </aside>

      {/* Mobile drawer */}
      <AnimatePresence data-id="element-200" data-mp="2lpcd0fogqlyw">
        {mobileOpen && <>
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} onClick={onCloseMobile} className="fixed inset-0 z-40 bg-slate-900/50 backdrop-blur-sm lg:hidden" data-id="element-201" data-mp="2lr5hppfimw49" />
            <motion.aside initial={{
          x: -300,
          opacity: 0.6
        }} animate={{
          x: 0,
          opacity: 1
        }} exit={{
          x: -300,
          opacity: 0
        }} transition={{
          type: 'spring',
          stiffness: 320,
          damping: 32
        }} className="glass-strong fixed inset-y-0 left-0 z-50 flex w-[272px] flex-col rounded-r-3xl py-5 lg:hidden" aria-label="Sidebar" data-id="element-202" data-mp="2dfwt264mtby4">
              <div className="mb-6 flex items-center justify-between px-4" data-id="element-203" data-mp="2w7pkvx7ysr49">
                <BrandMark size="sm" data-id="element-204" data-mp="3157y3zt0lsi2" />
                <button type="button" onClick={onCloseMobile} aria-label="Close navigation" className="grid h-8 w-8 place-items-center rounded-lg text-muted-foreground transition-colors hover:text-foreground" data-id="element-205" data-mp="3146j5k8volv2">
                  <X className="h-4 w-4" data-id="element-206" data-mp="1btfk1o06p190" />
                </button>
              </div>
              <NavItems collapsed={false} onNavigate={onCloseMobile} idPrefix="drawer" data-id="element-207" data-mp="1grqspveqt5pn" />
              <div className="mt-auto flex flex-col gap-3" data-id="element-208" data-mp="187u6d0172zcw">
                <LiveBadge totalStudents={totalStudents} collapsed={false} data-id="element-209" data-mp="148d2uc5bwcad" />
                <LogoutButton collapsed={false} data-id="element-210" data-mp="1i7l2151hrkek" />
              </div>
            </motion.aside>
          </>}
      </AnimatePresence>
    </>;
}